import { useState } from "react";
import {
	Button,
	Checkbox,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
	CircularProgress,
} from "@mui/material";
import { useCentralStore } from "../../../store/Central";
import { showNotification } from "../../../store/Central/selectors";
import { supabase } from "@/lib/supabase";
import { CompletedJobDocumentType } from "@/lib/supabase/supabaseTypes";
import {
	calculateJobTotals,
	ExtendedJobDocument,
	JobItemsFromExtendedView,
} from "../../../lib/utils/calculate";
import { JobItemTypeEnum } from "@/lib/supabase/supabaseEnums";
import { CalculatedAmountCell } from "./calculated-amount-cell.component";
import { useJobSheet } from "../../../store/Sheets/sheet-hooks/useJobSheet";
import { ExternalLink } from "lucide-react";
import { SupabaseShareEntityViewEnum } from "@/lib/supabase/supabaseTypes";
import { Logger } from "@/lib/logger/Logger";
import { getClientDisplayName } from "@/lib/utils/client-utils";

interface MigrateAmountsTableProps {
	jobDocuments: CompletedJobDocumentType[];
	jobsWithMissingOrgSettings: CompletedJobDocumentType[];
	onRefresh: () => void;
	mode: "amounts" | "orgSettings";
}

export const MigrateAmountsTable: React.FC<MigrateAmountsTableProps> = ({
	jobDocuments,
	jobsWithMissingOrgSettings,
	onRefresh,
	mode,
}) => {
	const { organization, clientsLookup } = useCentralStore((state) => ({
		organization: state.organization,
		clientsLookup: state.clientsLookup,
	}));

	const displayData =
		mode === "amounts" ? jobDocuments : jobsWithMissingOrgSettings;

	const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
	const [updating, setUpdating] = useState<boolean>(false);
	const [extendedDataCache, setExtendedDataCache] = useState<
		Map<number, ExtendedJobDocument>
	>(new Map());

	const { openJobSheet } = useJobSheet();

	// Handle opening job sheet
	const handleOpenJobSheet = async (jobDoc: CompletedJobDocumentType) => {
		const { data: job, error: jobError } = await supabase
			.from(SupabaseShareEntityViewEnum.JOBS_WITH_SHARES)
			.select("*")
			.eq("id", jobDoc.job_id ?? 0)
			.single();

		if (jobError) {
			Logger.error("Error fetching job:" + jobError);
			showNotification({
				message: "Auftrag konnte nicht gefunden werden",
				type: "error",
			});
			return;
		}

		openJobSheet({
			job: job,
			onShareFile: async (fileId: string) => {},
			onUnshareFile: async (fileId: string) => {},
			onAcceptJobRequest: async () => ({
				success: false as const,
				error: "not supported",
			}),
			onDenyJobRequest: async (channelId: string | null) => false,
		});
	};

	// Convert raw database job_items to JobItemsForCalculations[]
	const convertJobItemsFromJson = (
		jobItems: any
	): JobItemsFromExtendedView[] => {
		if (!jobItems || !Array.isArray(jobItems)) {
			return [];
		}

		return jobItems.map((item: any) => ({
			id: String(item.id),
			price: Number(item.price) || 0,
			quantity: Number(item.quantity) || 0,
			tp_value: Number(item.tp_value) || 0,
			discount: Number(item.discount) || 0,
			is_req_dr_tax: Boolean(item.is_req_dr_tax),
			is_warranty: Boolean(item.is_warranty),
			type: item.type as JobItemTypeEnum,
			code: "112",
			code_e: "112",
			description: "description",
			note: "",
		}));
	};

	// Fetch extended job document data for calculations
	const fetchExtendedJobDocument = async (
		jobDocumentId: number
	): Promise<ExtendedJobDocument | null> => {
		// Check cache first
		if (extendedDataCache.has(jobDocumentId)) {
			return extendedDataCache.get(jobDocumentId)!;
		}

		try {
			const { data, error } = await supabase
				.from("job_documents_extended_view")
				.select("*")
				.eq("job_document_id", jobDocumentId)
				.single();

			if (error) {
				Logger.error("Error fetching extended job document:" + error);
				return null;
			}

			// Convert the raw data to ExtendedJobDocument format
			const extendedJobDocument: ExtendedJobDocument = {
				...data,
				job_items: convertJobItemsFromJson(data.job_items),
			};

			// Cache the result
			setExtendedDataCache(
				(prev) => new Map(prev.set(jobDocumentId, extendedJobDocument))
			);
			return extendedJobDocument;
		} catch (error) {
			Logger.error("Error fetching extended job document:" + error);
			return null;
		}
	};

	// Calculate amount for a job document using existing frontend logic
	const calculateAmount = async (
		jobDoc: CompletedJobDocumentType
	): Promise<number> => {
		if (!organization || !jobDoc.id) return 0;

		// Fetch extended data needed for calculations
		const extendedJobDocument = await fetchExtendedJobDocument(jobDoc.id);
		if (!extendedJobDocument) return 0;

		const client = clientsLookup[jobDoc.client_id || ""];
		if (!client) return 0;

		const result = calculateJobTotals({
			extendedJobDocument,
			organization,
			client,
		});

		return result.total.value;
	};

	const handleSelectAll = () => {
		if (selectedIds.size === displayData.length) {
			setSelectedIds(new Set());
		} else {
			setSelectedIds(
				new Set(displayData.map((doc) => doc.id!).filter(Boolean))
			);
		}
	};

	const handleSelectOne = (id: number) => {
		const newSelected = new Set(selectedIds);
		if (newSelected.has(id)) {
			newSelected.delete(id);
		} else {
			newSelected.add(id);
		}
		setSelectedIds(newSelected);
	};

	interface RpcJobDefaultValues {
		success: boolean;
		message?: string;
		mwst_number: string;
		is_mwst_eust: boolean;
		round_to: number;
	}

	const getOrgSettingsFromRpc = async (
		clientId: string,
		organizationId: string
	): Promise<{
		mwst_number: string;
		is_mwst_eust: boolean;
		round_to: number;
	} | null> => {
		const { data, error } = await supabase.rpc("get_job_default_values", {
			p_client_id: clientId,
			p_organization_id: organizationId,
		});

		const result = data as RpcJobDefaultValues | null;

		if (error || !result?.success) {
			Logger.error(
				"Error getting default values: " +
					(error?.message || result?.message)
			);
			return null;
		}

		return {
			mwst_number: result.mwst_number,
			is_mwst_eust: result.is_mwst_eust,
			round_to: result.round_to,
		};
	};

	const updateSelectedItems = async () => {
		if (selectedIds.size === 0) {
			showNotification({
				message:
					mode === "amounts"
						? "Keine Lieferscheine ausgewählt"
						: "Keine Aufträge ausgewählt",
				type: "warning",
			});
			return;
		}

		const confirmMessage =
			mode === "amounts"
				? `Möchten Sie die Beträge für ${selectedIds.size} ausgewählte Lieferscheine wirklich aktualisieren?`
				: `Möchten Sie die Organisationseinstellungen für ${selectedIds.size} ausgewählte Aufträge wirklich aktualisieren?`;

		if (!confirm(confirmMessage)) return;

		setUpdating(true);

		try {
			const selectedDocs = displayData.filter((doc) =>
				selectedIds.has(doc.id!)
			);
			let successCount = 0;
			let errorCount = 0;

			for (const doc of selectedDocs) {
				try {
					if (mode === "amounts") {
						const amount = await calculateAmount(doc);
						const { error } = await supabase
							.from("job_documents")
							.update({ amount })
							.eq("id", doc.id!);

						if (error) {
							Logger.error(
								"Error updating job document:" + doc.id + error
							);
							errorCount++;
						} else {
							successCount++;
						}
					} else {
						if (!doc.client_id || !organization?.id) {
							errorCount++;
							continue;
						}

						const orgSettings = await getOrgSettingsFromRpc(
							doc.client_id,
							organization.id
						);
						if (!orgSettings) {
							errorCount++;
							continue;
						}

						const { error } = await supabase
							.from("jobs")
							.update({
								mwst_number: orgSettings.mwst_number,
								is_mwst_eust: orgSettings.is_mwst_eust,
								round_to: orgSettings.round_to,
							})
							.eq("id", doc.job_id!);

						if (error) {
							Logger.error(
								"Error updating job:" + doc.job_id + error
							);
							errorCount++;
						} else {
							successCount++;
						}
					}
				} catch (calcError) {
					Logger.error("Error updating:" + doc.id + calcError);
					errorCount++;
				}
			}

			const successMessage =
				mode === "amounts"
					? `${successCount} Beträge erfolgreich aktualisiert`
					: `${successCount} Aufträge erfolgreich aktualisiert`;

			if (successCount > 0) {
				showNotification({ message: successMessage, type: "success" });
			}
			if (errorCount > 0) {
				showNotification({
					message: `${errorCount} Fehler beim Aktualisieren`,
					type: "error",
				});
			}

			setSelectedIds(new Set());
			onRefresh();
		} catch (error) {
			Logger.error("Error in bulk update:" + error);
			showNotification({
				message: "Fehler beim Aktualisieren",
				type: "error",
			});
		} finally {
			setUpdating(false);
		}
	};

	const updateAllItems = async () => {
		const confirmMessage =
			mode === "amounts"
				? `Möchten Sie die Beträge für ALLE ${displayData.length} Lieferscheine wirklich aktualisieren?`
				: `Möchten Sie die Organisationseinstellungen für ALLE ${displayData.length} Aufträge wirklich aktualisieren?`;

		if (!confirm(confirmMessage)) return;

		setUpdating(true);

		try {
			let successCount = 0;
			let errorCount = 0;

			for (const doc of displayData) {
				try {
					if (mode === "amounts") {
						const amount = await calculateAmount(doc);
						const { error } = await supabase
							.from("job_documents")
							.update({ amount })
							.eq("id", doc.id!);

						if (error) {
							Logger.error(
								"Error updating job document:" + doc.id + error
							);
							errorCount++;
						} else {
							successCount++;
						}
					} else {
						if (!doc.client_id || !organization?.id) {
							errorCount++;
							continue;
						}

						const orgSettings = await getOrgSettingsFromRpc(
							doc.client_id,
							organization.id
						);
						if (!orgSettings) {
							errorCount++;
							continue;
						}

						const { error } = await supabase
							.from("jobs")
							.update({
								mwst_number: orgSettings.mwst_number,
								is_mwst_eust: orgSettings.is_mwst_eust,
								round_to: orgSettings.round_to,
							})
							.eq("id", doc.job_id!);

						if (error) {
							Logger.error(
								"Error updating job:" + doc.job_id + error
							);
							errorCount++;
						} else {
							successCount++;
						}
					}
				} catch (calcError) {
					Logger.error("Error updating:" + doc.id + calcError);
					errorCount++;
				}

				if ((successCount + errorCount) % 50 === 0) {
					showNotification({
						message: `Fortschritt: ${successCount + errorCount}/${
							displayData.length
						}`,
						type: "info",
					});
				}
			}

			const successMessage =
				mode === "amounts"
					? `${successCount} Beträge erfolgreich aktualisiert`
					: `${successCount} Aufträge erfolgreich aktualisiert`;

			if (successCount > 0) {
				showNotification({ message: successMessage, type: "success" });
			}
			if (errorCount > 0) {
				showNotification({
					message: `${errorCount} Fehler beim Aktualisieren`,
					type: "error",
				});
			}

			setSelectedIds(new Set());
			onRefresh();
		} catch (error) {
			Logger.error("Error in bulk update:" + error);
			showNotification({
				message: "Fehler beim Aktualisieren",
				type: "error",
			});
		} finally {
			setUpdating(false);
		}
	};

	if (displayData.length === 0) {
		return null;
	}

	const buttonLabelSelected =
		mode === "amounts"
			? `Ausgewählte Beträge aktualisieren (${selectedIds.size})`
			: `Ausgewählte Einstellungen aktualisieren (${selectedIds.size})`;

	const buttonLabelAll =
		mode === "amounts"
			? `Alle Beträge aktualisieren (${displayData.length})`
			: `Alle Einstellungen aktualisieren (${displayData.length})`;

	return (
		<div className="flex flex-col gap-4">
			<div className="flex gap-2 items-center">
				<Button
					variant="contained"
					color="primary"
					onClick={updateSelectedItems}
					disabled={updating || selectedIds.size === 0}
				>
					{updating ? (
						<CircularProgress size={20} color="inherit" />
					) : (
						buttonLabelSelected
					)}
				</Button>
				<Button
					variant="outlined"
					color="primary"
					onClick={updateAllItems}
					disabled={updating}
				>
					{updating ? (
						<CircularProgress size={20} color="inherit" />
					) : (
						buttonLabelAll
					)}
				</Button>
			</div>

			<Table>
				<TableHead>
					<TableRow>
						<TableCell padding="checkbox">
							<Checkbox
								checked={
									selectedIds.size === displayData.length &&
									displayData.length > 0
								}
								indeterminate={
									selectedIds.size > 0 &&
									selectedIds.size < displayData.length
								}
								onChange={handleSelectAll}
								disabled={updating}
							/>
						</TableCell>
						<TableCell>Auftragsnummer</TableCell>
						<TableCell>Auftragstitel</TableCell>
						<TableCell>Kunde</TableCell>
						<TableCell>Datum</TableCell>
						{mode === "amounts" ? (
							<>
								<TableCell>Aktueller Betrag</TableCell>
								<TableCell>Berechneter Betrag</TableCell>
							</>
						) : (
							<>
								<TableCell>MwSt.-Nr.</TableCell>
								<TableCell>EUSt.</TableCell>
								<TableCell>Rundung</TableCell>
							</>
						)}
						<TableCell>Aktionen</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{displayData.map((doc) => {
						const client = clientsLookup[doc.client_id || ""];

						return (
							<TableRow key={doc.id}>
								<TableCell padding="checkbox">
									<Checkbox
										checked={selectedIds.has(doc.id!)}
										onChange={() =>
											handleSelectOne(doc.id!)
										}
										disabled={updating}
									/>
								</TableCell>
								<TableCell>
									<Typography
										variant="body2"
										fontWeight="medium"
									>
										{doc.job_code}
									</Typography>
								</TableCell>
								<TableCell>
									<Typography variant="body2">
										{doc.job_title}
									</Typography>
								</TableCell>
								<TableCell>
									<Typography variant="body2">
										{client
											? getClientDisplayName(client)
											: "Unbekannt"}
									</Typography>
								</TableCell>
								<TableCell>
									<Typography variant="body2">
										{doc.date
											? new Date(
													doc.date
												).toLocaleDateString("de-DE")
											: "-"}
									</Typography>
								</TableCell>
								{mode === "amounts" ? (
									<>
										<TableCell>
											<Typography
												variant="body2"
												color="error"
											>
												{doc.amount ?? "Fehlend"}
											</Typography>
										</TableCell>
										<TableCell>
											<CalculatedAmountCell
												jobDoc={doc}
												calculateAmount={
													calculateAmount
												}
											/>
										</TableCell>
									</>
								) : (
									<>
										<TableCell>
											<Typography
												variant="body2"
												color={
													doc.job_mwst_number
														? "textPrimary"
														: "error"
												}
											>
												{doc.job_mwst_number ??
													"Fehlend"}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography
												variant="body2"
												color={
													doc.job_is_mwst_eust !==
													null
														? "textPrimary"
														: "error"
												}
											>
												{doc.job_is_mwst_eust !== null
													? doc.job_is_mwst_eust
														? "Ja"
														: "Nein"
													: "Fehlend"}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography
												variant="body2"
												color={
													doc.job_round_to !== null
														? "textPrimary"
														: "error"
												}
											>
												{doc.job_round_to ?? "Fehlend"}
											</Typography>
										</TableCell>
									</>
								)}
								<TableCell>
									<Button
										variant="outlined"
										size="small"
										startIcon={
											<ExternalLink className="w-4 h-4" />
										}
										onClick={() => handleOpenJobSheet(doc)}
										disabled={updating}
									>
										Auftrag öffnen
									</Button>
								</TableCell>
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
		</div>
	);
};
